import React, { useMemo, useState } from 'react';
import { collection, getDocs, query, updateDoc, where, doc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../config/firebase';
import { PrimaryCamper, Dependant, Note } from '../../../types/camper';
import { useAuth } from '../../../context/AuthContext';
import './MergeCamperRecordsPage.css';

type HistoryAction = 'keep_target' | 'replace_with_past' | 'merge_both';

interface AccommodationHistoryEntry {
  year: number | string;
  placeCode: string;
}

interface CamperRecord extends Partial<PrimaryCamper> {
  recordId: string;
  uid?: string | null;
  Town?: string;
  accommodationHistory?: AccommodationHistoryEntry[];
}

const normalize = (value: string): string => value.trim().toLowerCase();

const nameKey = (dep: Partial<Dependant>): string => {
  const first = normalize(dep.FirstName || '');
  const last = normalize(dep.LastName || '');
  return `${first}|${last}`;
};

const formatDate = (dateValue: any): string => {
  if (!dateValue) return 'n/a';
  if (typeof dateValue?.toDate === 'function') {
    return dateValue.toDate().toLocaleDateString();
  }
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return 'n/a';
  return parsed.toLocaleDateString();
};

const formatHistory = (history: AccommodationHistoryEntry[]): AccommodationHistoryEntry[] => {
  return history
    .filter((entry) => entry && entry.year !== undefined && entry.year !== null)
    .map((entry) => ({
      year: Number(entry.year),
      placeCode: entry.placeCode || 'No History'
    }))
    .sort((a, b) => Number(b.year) - Number(a.year))
    .slice(0, 8);
};

const MergeCamperRecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [pastSearchTerm, setPastSearchTerm] = useState('');
  const [currentMatches, setCurrentMatches] = useState<CamperRecord[]>([]);
  const [pastMatches, setPastMatches] = useState<CamperRecord[]>([]);
  const [selectedCurrent, setSelectedCurrent] = useState<CamperRecord | null>(null);
  const [selectedPast, setSelectedPast] = useState<CamperRecord | null>(null);
  const [historyAction, setHistoryAction] = useState<HistoryAction | ''>('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [loadingPast, setLoadingPast] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const targetHasHistory = (selectedCurrent?.accommodationHistory || []).length > 0;
  const sourceHasHistory = (selectedPast?.accommodationHistory || []).length > 0;
  const needsHistoryDecision = targetHasHistory && sourceHasHistory;

  const selectedPairLabel = useMemo(() => {
    if (!selectedCurrent || !selectedPast) return '';
    return `${selectedPast.FirstName || ''} ${selectedPast.LastName || ''} (${selectedPast.email || 'no email'}) -> ${selectedCurrent.FirstName || ''} ${selectedCurrent.LastName || ''} (${selectedCurrent.email || 'no email'})`;
  }, [selectedCurrent, selectedPast]);

  const loadAllActiveCampers = async (): Promise<CamperRecord[]> => {
    const campersQuery = query(collection(db, 'primaryCampers'), where('deleted', '==', false));
    const snapshot = await getDocs(campersQuery);
    return snapshot.docs.map((docSnap) => ({
      ...(docSnap.data() as CamperRecord),
      recordId: docSnap.id
    }));
  };

  const handleFindCurrent = async () => {
    const term = normalize(currentSearchTerm);
    setError('');
    setSuccess('');
    setCurrentMatches([]);
    setSelectedCurrent(null);
    setConfirmChecked(false);

    if (!term) {
      setError('Please enter a current email, first name, or last name.');
      return;
    }

    setLoadingCurrent(true);
    try {
      const allCampers = await loadAllActiveCampers();
      const matches = allCampers.filter((camper) => {
        const firstName = normalize(camper.FirstName || '');
        const lastName = normalize(camper.LastName || '');
        const email = normalize(camper.email || '');
        return firstName.includes(term) || lastName.includes(term) || email.includes(term);
      });
      setCurrentMatches(matches);
      if (matches.length === 1) {
        setSelectedCurrent(matches[0]);
      } else if (matches.length === 0) {
        setError('No active camper record matched that current search.');
      }
    } catch (err) {
      console.error('Error searching current camper:', err);
      setError('Failed to search current camper.');
    } finally {
      setLoadingCurrent(false);
    }
  };

  const handleFindPast = async () => {
    const term = normalize(pastSearchTerm);
    setError('');
    setSuccess('');
    setPastMatches([]);
    setSelectedPast(null);
    setConfirmChecked(false);

    if (!term) {
      setError('Please enter a past email, first name, or last name.');
      return;
    }

    setLoadingPast(true);
    try {
      const allCampers = await loadAllActiveCampers();
      const matches = allCampers.filter((camper) => {
        const firstName = normalize(camper.FirstName || '');
        const lastName = normalize(camper.LastName || '');
        const email = normalize(camper.email || '');
        return firstName.includes(term) || lastName.includes(term) || email.includes(term);
      });

      setPastMatches(matches);
      if (matches.length === 1) {
        setSelectedPast(matches[0]);
      } else if (matches.length === 0) {
        setError('No active camper record matched that past search.');
      }
    } catch (err) {
      console.error('Error searching past camper:', err);
      setError('Failed to search past camper.');
    } finally {
      setLoadingPast(false);
    }
  };

  const buildMergedDependants = (target: Dependant[], source: Dependant[]): Dependant[] => {
    const result: Dependant[] = [...target];
    const existing = new Set(target.map((dep) => nameKey(dep)));

    source.forEach((dep) => {
      const key = nameKey(dep);
      if (!key || key === '|') return;
      if (!existing.has(key)) {
        result.push(dep);
        existing.add(key);
      }
    });

    return result;
  };

  const buildHistory = (
    targetHistory: AccommodationHistoryEntry[],
    sourceHistory: AccommodationHistoryEntry[],
    action: HistoryAction
  ): AccommodationHistoryEntry[] => {
    const formattedTarget = formatHistory(targetHistory);
    const formattedSource = formatHistory(sourceHistory);

    if (action === 'keep_target') return formattedTarget;
    if (action === 'replace_with_past') return formattedSource;

    const byYear = new Map<number, AccommodationHistoryEntry>();
    formattedTarget.forEach((entry) => byYear.set(Number(entry.year), entry));
    formattedSource.forEach((entry) => {
      const year = Number(entry.year);
      if (!byYear.has(year)) {
        byYear.set(year, entry);
      }
    });

    return Array.from(byYear.values())
      .sort((a, b) => Number(b.year) - Number(a.year))
      .slice(0, 8);
  };

  const buildPersistentNote = (
    content: string,
    createdBy: string
  ): Note => {
    const now = new Date();
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    return {
      id,
      content,
      createdBy,
      createdDate: now,
      lastModifiedDate: now,
      isPersistent: true
    };
  };

  const handleMerge = async () => {
    if (!selectedCurrent || !selectedPast) {
      setError('Please select both current and past camper records.');
      return;
    }

    if (selectedCurrent.recordId === selectedPast.recordId) {
      setError('Current and past records cannot be the same camper.');
      return;
    }

    if (!confirmChecked) {
      setError('Please confirm before merging.');
      return;
    }

    if (needsHistoryDecision && !historyAction) {
      setError('Please choose how to handle stay history before merging.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to merge these two accounts?\n\n${selectedPairLabel}`
    );
    if (!confirmed) return;

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const targetDependants = (selectedCurrent.dependants || []) as Dependant[];
      const sourceDependants = (selectedPast.dependants || []) as Dependant[];
      const targetHistory = (selectedCurrent.accommodationHistory || []) as AccommodationHistoryEntry[];
      const sourceHistory = (selectedPast.accommodationHistory || []) as AccommodationHistoryEntry[];
      const finalHistoryAction: HistoryAction = needsHistoryDecision
        ? (historyAction as HistoryAction)
        : 'merge_both';
      const mergedDependants = buildMergedDependants(targetDependants, sourceDependants);
      const mergedHistory = buildHistory(targetHistory, sourceHistory, finalHistoryAction);

      const actor = currentUser?.uid || 'admin';
      const sourceEmail = selectedPast.email || 'unknown';
      const targetEmail = selectedCurrent.email || 'unknown';
      const targetNote = buildPersistentNote(
        `Merged camper data from past account ${sourceEmail} on ${new Date().toLocaleString()}. Dependants and accommodation history were reviewed and merged.`,
        actor
      );
      const sourceNote = buildPersistentNote(
        `Camper data was merged into current account ${targetEmail} on ${new Date().toLocaleString()}.`,
        actor
      );

      await updateDoc(doc(db, 'primaryCampers', selectedCurrent.recordId), {
        dependants: mergedDependants,
        accommodationHistory: mergedHistory,
        notes: [...((selectedCurrent.notes || []) as Note[]), targetNote],
        lastModifiedDate: serverTimestamp()
      });

      await updateDoc(doc(db, 'primaryCampers', selectedPast.recordId), {
        notes: [...((selectedPast.notes || []) as Note[]), sourceNote],
        lastModifiedDate: serverTimestamp()
      });

      setSelectedCurrent((prev) => (prev ? {
        ...prev,
        dependants: mergedDependants,
        accommodationHistory: mergedHistory,
        notes: [...((prev.notes || []) as Note[]), targetNote]
      } : prev));
      setSelectedPast((prev) => (prev ? {
        ...prev,
        notes: [...((prev.notes || []) as Note[]), sourceNote]
      } : prev));

      setSuccess('');
      setConfirmChecked(false);
      window.alert('Merge complete. Persistent notes were added to both records.');
      navigate('/admin/campers');
    } catch (err) {
      console.error('Error merging camper records:', err);
      setError('Failed to merge camper records. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderCamperCard = (camper: CamperRecord | null, label: string) => {
    if (!camper) {
      return (
        <div className="merge-camper-card merge-camper-card-empty">
          <h3>{label}</h3>
          <p>No record selected yet.</p>
        </div>
      );
    }

    const dependants = camper.dependants || [];
    const history = formatHistory(camper.accommodationHistory || []);

    return (
      <div className="merge-camper-card">
        <h3>{label}</h3>
        <div className="camper-main">
          <div className="camper-name">{`${camper.FirstName || ''} ${camper.LastName || ''}`.trim() || 'Unknown name'}</div>
          <div>{camper.email || 'No email'}</div>
          <div>{camper.MobilePhone || camper.HomePhone || 'No phone'}</div>
          <div>{camper.Town || 'No town'}</div>
        </div>

        <div className="camper-section">
          <strong>Dependants ({dependants.length})</strong>
          {dependants.length === 0 ? (
            <p className="muted">No dependants</p>
          ) : (
            <ul>
              {dependants.slice(0, 8).map((dep, idx) => (
                <li key={`${dep.FirstName}-${dep.LastName}-${idx}`}>
                  {dep.FirstName} {dep.LastName}
                </li>
              ))}
              {dependants.length > 8 && <li>...and {dependants.length - 8} more</li>}
            </ul>
          )}
        </div>

        <div className="camper-section">
          <strong>Stay History ({history.length})</strong>
          {history.length === 0 ? (
            <p className="muted">No stay history</p>
          ) : (
            <ul>
              {history.slice(0, 8).map((entry, idx) => (
                <li key={`${entry.year}-${entry.placeCode}-${idx}`}>
                  {entry.year}: {entry.placeCode}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="camper-section muted">
          Created: {formatDate(camper.createdDate)} | Updated: {formatDate(camper.lastModifiedDate)}
        </div>
      </div>
    );
  };

  return (
    <div className="merge-camper-page">
      <div className="merge-header">
        <h1>Merge 2 Camper Records</h1>
        <button className="back-button" onClick={() => navigate('/admin/campers')}>
          Back to Camper Management
        </button>
      </div>

      <div className="merge-search-grid">
        <div className="search-panel">
          <h2>Current Email (Target)</h2>
          <p>Search by current email, first name, or last name.</p>
          <div className="search-row">
            <input
              type="text"
              placeholder="Enter current email or name"
              value={currentSearchTerm}
              onChange={(e) => setCurrentSearchTerm(e.target.value)}
            />
            <button onClick={handleFindCurrent} disabled={loadingCurrent}>
              {loadingCurrent ? 'Searching...' : 'Find'}
            </button>
          </div>
          {currentMatches.length > 1 && (
            <div className="match-list">
              <p>Multiple records found. Select one:</p>
              {currentMatches.map((match) => (
                <button
                  key={`current-${match.recordId}`}
                  className={`match-item ${selectedCurrent?.recordId === match.recordId ? 'selected' : ''}`}
                  onClick={() => setSelectedCurrent(match)}
                >
                  {match.FirstName} {match.LastName} - {match.email || 'No email'}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="search-panel">
          <h2>Past Email (Source)</h2>
          <p>Search by past email, first name, or last name.</p>
          <div className="search-row">
            <input
              type="text"
              placeholder="Enter past email or name"
              value={pastSearchTerm}
              onChange={(e) => setPastSearchTerm(e.target.value)}
            />
            <button onClick={handleFindPast} disabled={loadingPast}>
              {loadingPast ? 'Searching...' : 'Search'}
            </button>
          </div>
          {pastMatches.length > 1 && (
            <div className="match-list">
              <p>Multiple records found. Select one:</p>
              {pastMatches.map((match) => (
                <button
                  key={`past-${match.recordId}`}
                  className={`match-item ${selectedPast?.recordId === match.recordId ? 'selected' : ''}`}
                  onClick={() => setSelectedPast(match)}
                >
                  {match.FirstName} {match.LastName} - {match.email || 'No email'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="merge-preview-grid">
        {renderCamperCard(selectedCurrent, 'Target Record (Current Email)')}
        {renderCamperCard(selectedPast, 'Source Record (Past Email)')}
      </div>

      {needsHistoryDecision && (
        <div className="history-decision">
          <h3>Stay History Decision Needed</h3>
          <p>Both records already have stay history. Choose how to handle target stay history for this merge.</p>
          <label>
            <input
              type="radio"
              name="historyAction"
              value="keep_target"
              checked={historyAction === 'keep_target'}
              onChange={() => setHistoryAction('keep_target')}
            />
            Keep target history only
          </label>
          <label>
            <input
              type="radio"
              name="historyAction"
              value="replace_with_past"
              checked={historyAction === 'replace_with_past'}
              onChange={() => setHistoryAction('replace_with_past')}
            />
            Replace target history with past history
          </label>
          <label>
            <input
              type="radio"
              name="historyAction"
              value="merge_both"
              checked={historyAction === 'merge_both'}
              onChange={() => setHistoryAction('merge_both')}
            />
            Merge both histories (target wins if same year exists in both)
          </label>
        </div>
      )}

      <div className="merge-confirm">
        <label>
          <input
            type="checkbox"
            checked={confirmChecked}
            onChange={(e) => setConfirmChecked(e.target.checked)}
            disabled={!selectedCurrent || !selectedPast}
          />
          Are you sure you want to merge these two accounts?
        </label>
        <button
          className="merge-button"
          onClick={handleMerge}
          disabled={submitting || !selectedCurrent || !selectedPast}
        >
          {submitting ? 'Merging...' : 'Merge Records'}
        </button>
      </div>

      {error && <div className="merge-message error">{error}</div>}
      {success && <div className="merge-message success">{success}</div>}
    </div>
  );
};

export default MergeCamperRecordsPage;
